import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../../Loader";
import "../addons.css";
import AuthContext from "../../../contexts/AuthProvider";
import { API_URL } from "../../../utils/config";
const baseUrl = API_URL;

const EditFacility = ({ onClose, facility_id, venue_id }) => {
  const { t } = useTranslation("facilty");
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    tool_name: "",
    venue_id: "",
    type: "",
    cost: "",
    duration: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const isMounted = useRef(true);
const { isAuthenticated } = useContext(AuthContext);
   const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };
  const fetchVenues = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return [];
      }

      const res = await fetch(`${baseUrl}/api/v1/attnd-venue/assignedVenues`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });
      if (res.status === 401) {
        navigate("/login");
        throw new Error(t("editFacility.errors.unauthorized"));
      }
      if (!res.ok) {
        throw new Error(t("editFacility.errors.fetchFailed"));
      }
      const json = await res.json();
      return json.data;
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
         
        navigate("/login");
      }
      return [];
    }
  };

  const fetchFacility = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return null;
      }

      const res = await fetch(
        `${baseUrl}/api/v1/attnd-venue/facilities/${facility_id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        }
      );
      if (res.status === 401) {
        navigate("/login");
        throw new Error(t("editFacility.errors.unauthorized"));
      }
      if (!res.ok) {
        throw new Error(t("editFacility.errors.fetchFailed"));
      }
      const json = await res.json();
      return json.data;
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
         
        navigate("/login");
      }
      return null;
    }
  };

  const initializeFormFields = (facility) => {
    if (!isMounted.current) return;
    setFormData({
      tool_name: facility.tool_name,
      venue_id: facility.venue_id,
      type: facility.type,
      cost: facility.cost,
      duration: facility.duration,
    });
  };

  useEffect(() => {
    isMounted.current = true;
    (async () => {
      setLoading(true);
      const [venuesData, facilityData] = await Promise.all([
        fetchVenues(),
        fetchFacility(),
      ]);
      if (!isMounted.current) return;
      setVenues(venuesData);
      if (facilityData) initializeFormFields(facilityData);
      setLoading(false);
    })();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "tool_name":
        if (!value) return t("editFacility.errors.toolNameRequired");
        break;
      case "venue_id":
        if (!value) return t("editFacility.errors.venueRequired");
        break;
      case "type":
        if (!value) return t("editFacility.errors.typeRequired");
        break;
      case "cost":
        if (!value) return t("editFacility.errors.costRequired");
        if (parseFloat(value) <= 0) return t("editFacility.errors.costPositive");
        break;
      case "duration":
        if (!value) return t("editFacility.errors.durationRequired");
        break;
    }
    return "";
  };

  const isFormValid = () =>
    ["tool_name", "venue_id", "type", "cost", "duration"].every(
      (field) => !validateField(field, formData[field])
    );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMessages((prev) => ({
      ...prev,
      [name]: validateField(name, value) || undefined,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "-") e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const currentErrors = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, validateField(k, v)])
    );
    if (Object.values(currentErrors).some(Boolean)) {
      setErrorMessages(currentErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      if (!isAuthenticated) {
        navigate("/");
        return;
      }

      const payload = {
        tool_name: formData.tool_name,
        venue_id: Number(formData.venue_id),
        type: formData.type,
        cost: parseFloat(formData.cost),
        duration: formData.duration,
      };
      const res = await fetch(`${baseUrl}/api/v1/attnd-venue/facilities/${facility_id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        navigate("/login");
        throw new Error(t("editFacility.errors.unauthorized"));
      }
      if (!res.ok) {
        const errJson = await res.json();
        // Map backend errors if present
        if (errJson.errors) {
          const backendErrors = errJson.errors.reduce((acc, err) => {
            const field = Object.keys(err)[0];
            acc[field] = err[field];
            return acc;
          }, {});
          setErrorMessages(backendErrors);
        } else {
          throw new Error(errJson.error || t("editFacility.errors.fetchFailed"));
        }
        return;
      }

      await res.json();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMessages((prev) => ({
        ...prev,
        message: err.message,
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="invite-attendee-modal">
      <div className="form-invite-attendee">
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {t("editFacility.labels.toolName")}:
              </label>
              <input
                type="text"
                name="tool_name"
                className="form-input"
                placeholder={t("editFacility.placeholders.toolName")}
                value={formData.tool_name}
                onChange={handleChange}
                required
              />
              {errorMessages.tool_name && (
                <span className="error-messages">
                  {errorMessages.tool_name}
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t("editFacility.labels.venue")}:</label>
              <div className="form-select-container">
                <select
                  name="venue_id"
                  className="form-select"
                  value={formData.venue_id}
                  onChange={handleChange}
                  disabled={loading}
                  required
                >
                  <option value="">{t("editFacility.placeholders.selectVenue")}</option>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              {errorMessages.venue_id && (
                <span className="error-messages">{errorMessages.venue_id}</span>
              )}
            </div>
          </div>

          {loading && (
            <div className="addons-loader-container">
              <Loader />
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t("editFacility.labels.type")}:</label>
              <div className="form-select-container">
                <select
                  name="type"
                  className="form-select"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t("editFacility.placeholders.selectType")}</option>
                  {Object.entries(t("editFacility.options.type", { returnObjects: true })).map(
                    ([val, label]) => (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    )
                  )}
                </select>
              </div>
              {errorMessages.type && (
                <span className="error-messages">{errorMessages.type}</span>
              )}
            </div>
          </div>

          <div className="form-row" style={{ margin: 0 }}>
            <div className="form-group">
              <label className="form-label">{t("editFacility.labels.cost")}:</label>
              <input
                type="number"
                name="cost"
                className="form-input"
                placeholder={t("editFacility.placeholders.cost")}
                value={formData.cost}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
              />
              {errorMessages.cost && (
                <span className="error-messages">{errorMessages.cost}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t("editFacility.labels.duration")}:</label>
              <div className="form-select-container">
                <select
                  name="duration"
                  className="form-select"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t("editFacility.placeholders.selectDuration")}</option>
                  {Object.entries(t("editFacility.options.duration", { returnObjects: true })).map(
                    ([val, label]) => (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    )
                  )}
                </select>
              </div>
              {errorMessages.duration && (
                <span className="error-messages">{errorMessages.duration}</span>
              )}
            </div>
          </div>

          {errorMessages.message && (
            <p className="error-messages" style={{ marginBottom: "15px" }}>
              {errorMessages.message}
            </p>
          )}

          <div className="modal-footer">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
            >
              {t("editFacility.buttons.cancel")}
            </button>
            <button
              type="submit"
              className="invite-button"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting
                ? t("editFacility.buttons.submitting")
                : t("editFacility.buttons.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFacility;
